import React, { useState, useEffect, useMemo, memo } from 'react';
import { motion } from 'framer-motion';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { 
  FiSearch, 
  FiGrid, 
  FiList,
  FiUser,
  FiBox,
  FiLoader
} from 'react-icons/fi';
import { getAllMCPServers } from '../services/mcpService';

// Get type color
const getTypeColor = (type) => {
  const colors = {
    'search': 'from-blue-500 to-blue-600',
    'filesystem': 'from-green-500 to-green-600',
    'development': 'from-purple-500 to-purple-600',
    'data': 'from-red-500 to-red-600',
    'ai': 'from-pink-500 to-pink-600',
    'communication': 'from-yellow-500 to-yellow-600',
    'utility': 'from-indigo-500 to-indigo-600',
    'unknown': 'from-gray-500 to-gray-600'
  };
  return colors[type] || 'from-gray-500 to-gray-600';
};

// Server Card Component
const ServerCard = memo(({ server, index, onTagClick }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const maxLength = 150; // Characters to show before truncating
  const shouldTruncate = server.description && server.description.length > maxLength;
  const displayDescription = shouldTruncate && !isExpanded
    ? server.description.substring(0, maxLength) + '...'
    : server.description;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      className="group relative bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-secondary-500/50 transition-all duration-300 hover:shadow-xl hover:shadow-secondary-500/10"
    >
      {/* Server Type Badge */}
      <div className={`absolute top-4 right-4 px-3 py-1 bg-gradient-to-r ${getTypeColor(server.type)} rounded-full text-xs font-semibold text-white capitalize`}>
        {server.type}
      </div>

      {/* Server Icon */}
      <div className="w-16 h-16 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-xl flex items-center justify-center mb-4">
        <FiBox className="w-8 h-8 text-white" />
      </div>

      {/* Server Name */}
      <div className="mb-4">
        <h3 className="text-xl font-bold text-white mb-3 group-hover:text-secondary-400 transition-colors">
          {server.name}
        </h3>

        {/* Description */}
        <div className="mb-2">
          <p className="text-gray-300 text-sm leading-relaxed">
            {displayDescription}
          </p>
          {shouldTruncate && (
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="text-secondary-400 hover:text-secondary-300 text-xs font-semibold mt-2 transition-colors focus:outline-none"
            >
              {isExpanded ? 'Show Less' : 'Show More'}
            </button>
          )}
        </div>
      </div>

      {/* Author */}
      <div className="flex items-center mb-4 text-sm text-gray-400">
        <FiUser className="w-4 h-4 mr-2" />
        <span>by {server.author}</span>
      </div>

      {/* Tags */}
      {server.tags && server.tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {server.tags.map((tag, tagIndex) => (
            <span
              key={tagIndex}
              onClick={() => onTagClick(tag)}
              className="px-3 py-1 bg-white/10 text-gray-300 text-xs rounded-full hover:bg-secondary-500/20 cursor-pointer transition-colors"
            >
              #{tag}
            </span>
          ))}
        </div>
      )}
    </motion.div>
  );
});

const MCPListing = () => {
  const [servers, setServers] = useState([]);
  const [filteredServers, setFilteredServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTag, setSelectedTag] = useState('all');

  // Get all unique tags from servers
  const availableTags = useMemo(() => {
    const tags = servers.flatMap(server => server.tags || []);
    const uniqueTags = [...new Set(tags)].filter(tag => tag.toLowerCase() !== 'all').sort();
    return [
      { value: 'all', label: 'All Tags' },
      ...uniqueTags.map(tag => ({ value: tag, label: tag }))
    ];
  }, [servers]);

  const [viewMode, setViewMode] = useState('grid');

  // Fetch servers on component mount
  useEffect(() => {
    const fetchServers = async () => {
      try {
        setLoading(true);
        const data = await getAllMCPServers();
        setServers(data);
        setFilteredServers(data);
        setError(null);
      } catch (err) {
        setError(err.message);
        console.error('Error fetching servers:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchServers();
  }, []);

  // Handle search and filtering
  useEffect(() => {
    const filterServers = () => {
      let filtered = servers;

      // Filter by search term (search in name, description, author, tags)
      if (searchTerm) {
        filtered = filtered.filter(server =>
          server.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          server.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
          server.author.toLowerCase().includes(searchTerm.toLowerCase()) ||
          server.tags.some(tag => tag.toLowerCase().includes(searchTerm.toLowerCase()))
        );
      }

      // Filter by tag
      if (selectedTag !== 'all') {
        filtered = filtered.filter(server => 
          server.tags.some(tag => tag.toLowerCase() === selectedTag.toLowerCase())
        );
      }

      setFilteredServers(filtered);
    };

    filterServers();
  }, [searchTerm, selectedTag, servers]);

  // Get all unique tags
  const allTags = useMemo(() => {
    const tags = servers.flatMap(server => server.tags || []);
    return [...new Set(tags)].sort();
  }, [servers]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-dark-900 via-dark-800 to-secondary-900 text-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-full mb-8"
            >
              <FiBox className="w-10 h-10 text-white" />
            </motion.div>
            
            <h1 className="text-5xl md:text-6xl font-display font-bold mb-6">
              <span className="gradient-text">MCP Server Directory</span>
            </h1>
            
            <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed mb-8">
              Discover available Model Context Protocol servers available in Bervin!
            </p>

            {/* Stats */}
            <div className="flex justify-center gap-8 mb-12">
              <div className="text-center">
                <div className="text-3xl font-bold gradient-text">{servers.length}</div>
                <div className="text-gray-400">Available Servers</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold gradient-text">{allTags.length}</div>
                <div className="text-gray-400">Tags</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Filters and Search */}
      <section className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-2xl p-6 mb-8">
            <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
              {/* Search */}
              <div className="relative flex-1 max-w-md">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                <input
                  type="text"
                  placeholder="Search servers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 bg-dark-700/50 border border-white/10 rounded-xl text-white placeholder-gray-400 focus:border-secondary-500 focus:outline-none"
                />
              </div>

              {/* Tag Filter */}
              <select
                value={selectedTag}
                onChange={(e) => setSelectedTag(e.target.value)}
                className="px-4 py-3 bg-dark-700/50 border border-white/10 rounded-xl text-white focus:border-secondary-500 focus:outline-none"
              >
                {availableTags.map(tag => (
                  <option key={tag.value} value={tag.value} className="bg-dark-700">
                    {tag.label}
                  </option>
                ))}
              </select>

              {/* View Mode Toggle */}
              <div className="flex items-center space-x-2">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-3 rounded-xl transition-all ${
                    viewMode === 'grid' 
                      ? 'bg-secondary-500 text-white' 
                      : 'bg-white/10 text-gray-400 hover:text-white'
                  }`}
                >
                  <FiGrid className="w-5 h-5" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-3 rounded-xl transition-all ${
                    viewMode === 'list' 
                      ? 'bg-secondary-500 text-white' 
                      : 'bg-white/10 text-gray-400 hover:text-white'
                  }`}
                >
                  <FiList className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Active Filters */}
            {(selectedTag !== 'all' || searchTerm) && (
              <div className="flex flex-wrap gap-2 mt-4 pt-4 border-t border-white/10">
                {searchTerm && (
                  <span className="px-3 py-1 bg-secondary-500/20 text-secondary-300 text-sm rounded-full flex items-center">
                    Search: "{searchTerm}"
                    <button onClick={() => setSearchTerm('')} className="ml-2 hover:text-white">×</button>
                  </span>
                )}
                {selectedTag !== 'all' && (
                  <span className="px-3 py-1 bg-secondary-500/20 text-secondary-300 text-sm rounded-full flex items-center">
                    Tag: {selectedTag}
                    <button onClick={() => setSelectedTag('all')} className="ml-2 hover:text-white">×</button>
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <FiLoader className="w-8 h-8 animate-spin text-secondary-400" />
              <span className="ml-3 text-gray-300">Loading MCP servers...</span>
            </div>
          ) : error ? (
            <div className="text-center py-20">
              <div className="text-red-400 mb-4">Error loading servers: {error}</div>
              <button
                onClick={() => window.location.reload()}
                className="px-6 py-3 bg-gradient-to-r from-secondary-500 to-accent-500 rounded-xl text-white font-semibold hover:shadow-lg transition-all"
              >
                Retry
              </button>
            </div>
          ) : (
            <>
              {/* Results Count */}
              <div className="mb-6 text-gray-400">
                Showing {filteredServers.length} of {servers.length} servers
              </div>

              {/* Server Grid */}
              {filteredServers.length > 0 ? (
                <div className={`grid gap-6 ${
                  viewMode === 'grid' 
                    ? 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4' 
                    : 'grid-cols-1'
                }`}>
                  {filteredServers.map((server, index) => (
                    <ServerCard
                      key={server.id}
                      server={server}
                      index={index}
                      onTagClick={setSelectedTag}
                    />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <FiBox className="w-16 h-16 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-400 mb-2">No servers found</h3>
                  <p className="text-gray-500">Try adjusting your search criteria or filters</p>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MCPListing;